import { z } from 'zod';
import {
  commissioningThresholdAdmissionDecisionSchema, commissioningThresholdEconomicsTraceSchema,
  commissioningThresholdValiditySchema,
} from './commissioning-threshold-economics';

export const commissioningSyntheticScalarSchema = z.union([
  z.string().max(300), z.number().finite(), z.boolean(), z.null(),
]);
/** Values explicitly supplied by a user in a synthetic recipe scenario. They
 * are not claims about a saved competition, its VP, membership or sources. */
export const commissioningTemplateSimulationEventSchema = z.object({
  valenza: z.number().finite(),
  canone_value: z.number().finite().nonnegative().nullable().optional(),
  canone_l2_multiplier: z.number().finite().nonnegative().nullable().optional(),
  gettone_gara: z.number().finite().optional(),
  variables: z.record(commissioningSyntheticScalarSchema).optional(),
  id: z.string().uuid().optional(),
  occurred_at: z.string().datetime({ offset: true }).optional(),
  line_total: z.number().finite().nullable().optional(),
}).strict();
export const commissioningThresholdSyntheticEventSchema = commissioningTemplateSimulationEventSchema.extend({
  id: z.string().uuid(),
  occurred_at: z.string().datetime({ offset: true }),
});
export type CommissioningThresholdSyntheticEvent = z.infer<typeof commissioningThresholdSyntheticEventSchema>;

export const commissioningThresholdPeriodScenarioSchema = z.object({
  kind: z.literal('synthetic_period'),
  /** Explicit UTC calendar window; timestamps are ordered by instant/id. */
  period_start: z.string().date(),
  period_end: z.string().date(),
  target_value: z.number().finite().nullable().optional(),
  events_before: z.array(commissioningThresholdSyntheticEventSchema).max(1000),
}).strict();
export type CommissioningThresholdPeriodScenario = z.infer<typeof commissioningThresholdPeriodScenarioSchema>;

const nullableAmount = z.number().finite().nullable();
const periodSummary = z.object({
  eventCount: z.number().int().nonnegative(),
  countedCount: z.number().int().nonnegative().nullable(),
  payoutEur: nullableAmount,
  adjustmentsEur: nullableAmount,
  totalEur: nullableAmount,
  validity: commissioningThresholdValiditySchema,
}).strict();
export const commissioningThresholdScenarioRowResultSchema = z.object({
  eventId: z.string().uuid(),
  occurredAt: z.string().datetime({ offset: true }),
  validity: commissioningThresholdValiditySchema,
  counted: z.boolean().nullable(),
  remunerated: z.boolean().nullable(),
  valenza: z.number().finite().nullable(),
  beforeCapEur: nullableAmount,
  payoutEur: nullableAmount,
  flatEur: nullableAmount,
  admission: commissioningThresholdAdmissionDecisionSchema.nullable(),
  economics: commissioningThresholdEconomicsTraceSchema.nullable(),
}).strict();
export type CommissioningThresholdScenarioRowResult = z.infer<typeof commissioningThresholdScenarioRowResultSchema>;
export const commissioningThresholdPeriodComparisonSchema = z.object({
  context: z.literal('draft_synthetic_period'),
  fingerprint: z.string().regex(/^[a-f0-9]{64}$/),
  period: z.object({ start: z.string().date(), end: z.string().date(), timezone: z.literal('UTC') }).strict(),
  validity: commissioningThresholdValiditySchema,
  before: periodSummary,
  after: periodSummary,
  marginal: z.object({
    newEventEur: nullableAmount,
    repricingEur: nullableAmount,
    otherEffectsEur: nullableAmount,
    totalEur: nullableAmount,
  }).strict(),
  rows: z.array(z.object({
    eventId: z.string().uuid(),
    isNew: z.boolean(),
    before: commissioningThresholdScenarioRowResultSchema.nullable(),
    after: commissioningThresholdScenarioRowResultSchema,
    deltaEur: nullableAmount,
  }).strict()),
  warnings: z.array(z.string()),
}).strict();
export type CommissioningThresholdPeriodComparison = z.infer<typeof commissioningThresholdPeriodComparisonSchema>;
